import {
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { cn } from "../../lib/utils";
import { Tooltip } from "../../ui/Tooltip";
import { FormFieldError } from "../FormFieldError";
import { CheckboxInput } from "../inputs/CheckboxInput";

export type FormCheckboxGroupProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	options: { value: string; label: string; tooltip?: string }[];
	className?: string;
	labelClassName?: string;
	errorMessage?: string;
	noneValue?: string;
	compact?: boolean;
	testId?: string;
};

export const FormCheckboxGroup = <
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
>({
	control,
	defaultValue,
	disabled,
	name,
	options,
	className,
	labelClassName,
	errorMessage,
	noneValue,
	compact,
	testId,
}: FormCheckboxGroupProps<TFieldValues, TTransformedValues>) => {
	const {
		field,
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
		disabled,
	});

	const value = (field.value as string[]) ?? [];
	const isNoneSelected = noneValue ? value.includes(noneValue) : false;

	const handleOptionClick = (optionValue: string) => {
		if (disabled) return;

		const isChecked = value.includes(optionValue);
		const isNoneValue = optionValue === noneValue;

		let newValue: string[];
		if (isNoneValue) {
			newValue = isChecked ? [] : [noneValue];
		} else {
			newValue = isChecked ? value.filter((val) => val !== optionValue) : [...value, optionValue];
		}

		field.onChange(newValue);
		field.onBlur();
	};

	return (
		<div
			className={cn(
				"relative flex flex-col w-fit px-[25px] py-[16px] rounded-md border border-transparent",
				className,
				compact ? "py-[8px]" : undefined,
				error ? "border-red-500" : undefined,
			)}
		>
			{options.map((option) => {
				const checked = value.includes(option.value);
				const isNoneValue = option.value === noneValue;
				const isDisabled = disabled || (!isNoneValue && isNoneSelected);

				return (
					<div
						key={option.value}
						className={cn(
							"group flex items-center h-[29px] space-x-[14px]",
							isDisabled ? "cursor-default" : "cursor-pointer",
							compact ? "space-x-[11px]" : undefined,
						)}
						onClick={() => {
							if (!isDisabled) handleOptionClick(option.value);
						}}
					>
						<CheckboxInput
							className={cn(
								error
									? "outline-danger focus-within:outline-danger focus-within:hover:outline-danger hover:outline-danger hover:outline-2"
									: undefined,
							)}
							disabled={isDisabled}
							compact={compact}
							checked={checked}
							testId={testId ? `${testId}-${option.value}` : undefined}
						/>
						<span
							className={cn(
								"text-[15px] font-normal text-primary leading-[19px] flex items-center gap-[5px]",
								compact ? "text-[13px]" : undefined,
								labelClassName,
							)}
						>
							{option.label}
							{option.tooltip && (
								<Tooltip
									id={`tooltip-checkbox-${name}-${option.value}`}
									content={option.tooltip}
									condensed
								/>
							)}
						</span>
					</div>
				);
			})}
			<FormFieldError error={error} errorMessage={errorMessage} className="!mt-0 top-[-9px]" />
		</div>
	);
};
